import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { 
  DollarSign, TrendingUp, TrendingDown, FileText, Plus, X, Loader2, ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const currency = (value) => `KES ${Number(value || 0).toLocaleString()}`;

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-KE', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatPaymentMethod = (value) => {
  if (!value) return 'Not recorded';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const Finance = () => {
  const [summary, setSummary] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netIncome: 0,
    transactionCount: 0
  });
  const [transactions, setTransactions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('income');
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    category: 'utilities',
    payment_method: 'cash',
    expense_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const fetchFinanceData = useCallback(async () => {
    try {
      // Fetch all data in parallel
      const [transactionsRes, expensesRes] = await Promise.all([
        api.get('/financial/transactions'),
        api.get('/financial/expenses')
      ]);

      const transactionsData = transactionsRes.data || [];
      const expensesData = expensesRes.data || [];

      const totalRevenue = transactionsData.reduce((sum, row) => sum + Number(row.total_amount || 0), 0);
      const totalExpenses = expensesData.reduce((sum, row) => sum + Number(row.amount || 0), 0);

      setSummary({
        totalRevenue,
        totalExpenses,
        netIncome: totalRevenue - totalExpenses,
        transactionCount: transactionsData.length
      });
      setTransactions(transactionsData);
      setExpenses(expensesData);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load finance data:', error);
      toast.error('Failed to load finance data');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFinanceData();
  }, [fetchFinanceData]);

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/financial/expenses', expenseForm);
      toast.success('Expense recorded successfully');
      setShowAddExpense(false);
      setExpenseForm({
        description: '',
        amount: '',
        category: 'utilities',
        payment_method: 'cash',
        expense_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchFinanceData();
    } catch (error) {
      toast.error('Failed to record expense');
    }
    setSubmitting(false);
  };

  const receiptLink = (receiptNumber) => {
    return `/finance/receipt/${encodeURIComponent(receiptNumber)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-brand-blue-100 rounded-lg">
            <DollarSign size={24} className="text-brand-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-dark">Finance</h1>
            <p className="text-sm text-neutral-gray">Track revenue, expenses, and transactions</p>
          </div>
        </div>

        <button
          onClick={() => setShowAddExpense(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={18} />
          <span>Add Expense</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5">
          <div className="flex items-start justify-between mb-3">
            <p className="text-sm font-medium text-neutral-gray">Total Revenue</p>
            <div className="p-3 bg-brand-green-100 rounded-full">
              <TrendingUp className="text-brand-green" size={24} />
            </div>
          </div>
          <p className="text-2xl font-bold text-brand-green">KES {summary.totalRevenue.toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5">
          <div className="flex items-start justify-between mb-3">
            <p className="text-sm font-medium text-neutral-gray">Total Expenses</p>
            <div className="p-3 bg-brand-red-100 rounded-full">
              <TrendingDown className="text-brand-red" size={24} />
            </div>
          </div>
          <p className="text-2xl font-bold text-brand-red">KES {summary.totalExpenses.toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5">
          <div className="flex items-start justify-between mb-3">
            <p className="text-sm font-medium text-neutral-gray">Net Income</p>
            <div className={`p-3 rounded-full ${summary.netIncome >= 0 ? 'bg-brand-blue-100' : 'bg-brand-red-100'}`}>
              <DollarSign className={summary.netIncome >= 0 ? 'text-brand-blue' : 'text-brand-red'} size={24} />
            </div>
          </div>
          <p className={`text-2xl font-bold ${summary.netIncome >= 0 ? 'text-brand-blue' : 'text-brand-red'}`}>
            KES {summary.netIncome.toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5">
          <div className="flex items-start justify-between mb-3">
            <p className="text-sm font-medium text-neutral-gray">Transactions</p>
            <div className="p-3 bg-brand-purple-100 rounded-full">
              <FileText className="text-brand-purple" size={24} />
            </div>
          </div>
          <p className="text-2xl font-bold text-brand-purple">{summary.transactionCount}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-medium">
        <div className="border-b border-neutral-medium">
          <div className="flex">
            <button
              onClick={() => setActiveTab('income')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'income'
                  ? 'text-brand-green border-brand-green'
                  : 'text-neutral-gray border-transparent hover:text-gray-700'
              }`}
            >
              Income
            </button>
            <button
              onClick={() => setActiveTab('expenses')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'expenses'
                  ? 'text-brand-green border-brand-green'
                  : 'text-neutral-gray border-transparent hover:text-gray-700'
              }`}
            >
              Expenses
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'income' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-medium">
                    <th className="text-left text-sm font-medium text-neutral-gray pb-3">Receipt #</th>
                    <th className="text-left text-sm font-medium text-neutral-gray pb-3">Client</th>
                    <th className="text-left text-sm font-medium text-neutral-gray pb-3">Date</th>
                    <th className="text-left text-sm font-medium text-neutral-gray pb-3">Payment Method</th>
                    <th className="text-left text-sm font-medium text-neutral-gray pb-3">M-Pesa Code</th>
                    <th className="text-left text-sm font-medium text-neutral-gray pb-3">Status</th>
                    <th className="text-right text-sm font-medium text-neutral-gray pb-3">Amount</th>
                    <th className="text-right text-sm font-medium text-neutral-gray pb-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-neutral-gray">No transactions recorded</td>
                    </tr>
                  ) : transactions.map((row, index) => (
                    <tr key={index} className="border-b border-neutral-medium last:border-0 hover:bg-neutral-light transition-colors">
                      <td className="py-3 text-sm font-medium text-neutral-dark">
                        {row.receipt_number}
                      </td>
                      <td className="py-3 text-sm text-neutral-gray">
                        {row.client_name || 'Walk-in client'}
                      </td>
                      <td className="py-3 text-sm text-neutral-gray">
                        {formatDate(row.sale_date)}
                      </td>
                      <td className="py-3 text-sm capitalize text-neutral-gray">
                        {formatPaymentMethod(row.payment_method)}
                      </td>
                      <td className="py-3 text-sm font-mono font-semibold text-brand-blue uppercase">
                        {row.mpesa_confirmation_code || '-'}
                      </td>
                      <td className="py-3 text-sm capitalize text-neutral-gray">
                        {formatPaymentMethod(row.payment_status)}
                      </td>
                      <td className="py-3 text-sm text-right font-medium text-brand-green">
                        {currency(row.total_amount)}
                      </td>
                      <td className="py-3 text-sm text-right">
                        <Link
                          to={receiptLink(row.receipt_number)}
                          className="inline-flex items-center gap-1 text-brand-green hover:text-brand-green-700 font-medium"
                        >
                          <span>Receipt</span>
                          <ExternalLink size={14} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-medium">
                    <th className="text-left text-sm font-medium text-neutral-gray pb-3">Description</th>
                    <th className="text-left text-sm font-medium text-neutral-gray pb-3">Date</th>
                    <th className="text-left text-sm font-medium text-neutral-gray pb-3">Category</th>
                    <th className="text-left text-sm font-medium text-neutral-gray pb-3">Payment</th>
                    <th className="text-right text-sm font-medium text-neutral-gray pb-3">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-neutral-gray">No expenses recorded</td>
                    </tr>
                  ) : expenses.map((expense, index) => (
                    <tr key={index} className="border-b border-neutral-medium last:border-0">
                      <td className="py-3 text-sm text-neutral-dark">{expense.description}</td>
                      <td className="py-3 text-sm text-neutral-gray">
                        {expense.expense_date ? new Date(expense.expense_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-3 text-sm capitalize text-neutral-gray">{expense.category}</td>
                      <td className="py-3 text-sm capitalize text-neutral-gray">{expense.payment_method}</td>
                      <td className="py-3 text-sm text-right font-medium text-brand-red">
                        KES {Number(expense.amount || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Expense Modal */}
      {showAddExpense && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-neutral-medium flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-dark">Record Expense</h2>
              <button onClick={() => setShowAddExpense(false)} className="p-1 hover:bg-neutral-light rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleExpenseSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                <input
                  type="text"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Amount (KES)</label>
                  <input
                    type="number"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Category</label>
                  <select
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
                  >
                    <option value="utilities">Utilities</option>
                    <option value="rent">Rent</option>
                    <option value="salaries">Salaries</option>
                    <option value="supplies">Supplies</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="marketing">Marketing</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Method</label>
                  <select
                    value={expenseForm.payment_method}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, payment_method: e.target.value }))}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
                  >
                    <option value="cash">Cash</option>
                    <option value="mpesa">M-Pesa</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="card">Card</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Date</label>
                  <input
                    type="date"
                    value={expenseForm.expense_date}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, expense_date: e.target.value }))}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
                <textarea
                  value={expenseForm.notes}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-medium">
                <button
                  type="button"
                  onClick={() => setShowAddExpense(false)}
                  className="px-5 py-2.5 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-50"
                >
                  {submitting ? (
                    <span className="flex items-center space-x-2">
                      <Loader2 size={16} className="animate-spin" />
                      <span>Recording...</span>
                    </span>
                  ) : 'Record Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Finance;